import {
  allowedRentalTransitionsFor,
  bookingClashMessage,
  findBookingClash,
  type RentalItem,
  type RentalKind,
  type RentalOrder,
  type RentalStatus,
  type RentalTransport,
  type TransportStatus,
} from './model';
import { computeFinalBilling, computeMonthlyBilling, type RentalBilling } from './billing';

/** A rentable machine with its tiered rates (a machine flagged rentable in the Machines module). */
export interface FleetMachine {
  /** Maschinen-Id (für Detail-Abruf, z. B. Warengruppen-Auflösung beim Abrechnen); optional, da die
   *  Demo-/RPC-Flotte sie nicht führt — die aktive Flotte in RentalPage (aus dem Maschinenstamm) schon. */
  readonly id?: string;
  readonly machineNumber: string;
  readonly machineLabel: string;
  readonly dailyRate: number;
  readonly weeklyRate: number;
  readonly monthlyRate: number;
  readonly minimumDays: number;
}

/**
 * Eine gewählte Maschine MIT den für diesen Auftrag geltenden Sätzen. Die Sätze werden im Formular
 * aus dem Maschinenstamm (Flotte) vorbelegt und sind pro Auftrag überschreibbar -- der Auftrag hält
 * fest, was tatsächlich vereinbart wurde, nicht was die Maschine „normalerweise" kostet.
 */
export interface RentalMachineInput {
  readonly machineNumber: string;
  /** Anzeigename der Maschine (aus dem Maschinenstamm) -- der Auftrag hält ihn fest. */
  readonly machineLabel: string;
  readonly dailyRate: number;
  /** 0 = Staffel nicht angeboten. */
  readonly weeklyRate: number;
  readonly monthlyRate: number;
}

export interface CreateRentalOrderInput {
  readonly tenantId: string;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly siteAddress: string;
  readonly contactName: string;
  readonly contactPhone: string;
  readonly deliveryRequired: boolean;
  readonly pickupRequired: boolean;
  readonly depositAmount: number;
  readonly note: string;
  readonly machines: readonly RentalMachineInput[];
  readonly kind?: RentalKind;
  readonly intervalWeekday?: number | null;
  readonly transportCost?: number;
  /** true = als Mietangebot (Status 'request', reserviert die Maschine noch nicht); Standard = direkt 'reserved'. */
  readonly asOffer?: boolean;
}

/** Nachträgliche Änderung eines bestehenden Mietauftrags (nur vor der Auslieferung). */
export interface UpdateRentalOrderInput extends CreateRentalOrderInput {
  readonly rentalId: string;
}

export interface ReturnItemInput {
  readonly itemId: string;
  readonly meterIn: number | null;
  readonly damageNote: string;
  readonly damageAmount: number;
}

export interface RentalGateway {
  listFleet(tenantId: string): Promise<readonly FleetMachine[]>;
  listRentalOrders(tenantId: string): Promise<readonly RentalOrder[]>;
  createRentalOrder(input: CreateRentalOrderInput): Promise<RentalOrder>;
  updateRentalOrder(input: UpdateRentalOrderInput): Promise<RentalOrder>;
  transitionRental(input: { readonly tenantId: string; readonly rentalId: string; readonly toStatus: RentalStatus }): Promise<RentalOrder>;
  updateTransport(input: { readonly tenantId: string; readonly rentalId: string; readonly transportId: string; readonly plannedDate?: string; readonly driverName?: string; readonly status?: TransportStatus }): Promise<RentalOrder>;
  captureReturn(input: { readonly tenantId: string; readonly rentalId: string; readonly actualReturnDate: string; readonly items: readonly ReturnItemInput[] }): Promise<RentalOrder>;
  terminateEarly(input: { readonly tenantId: string; readonly rentalId: string; readonly terminationDate: string }): Promise<RentalOrder>;
  billMonth(input: { readonly tenantId: string; readonly rentalId: string }): Promise<{ order: RentalOrder; billing: RentalBilling }>;
  billFinal(input: { readonly tenantId: string; readonly rentalId: string }): Promise<{ order: RentalOrder; billing: RentalBilling }>;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
function orderNumber(seq: number): string {
  return `MI-26-${String(seq).padStart(6, '0')}`;
}
function transportNumber(seq: number): string {
  return `MT-26-${String(seq).padStart(6, '0')}`;
}

const fleetByTenant: Readonly<Record<string, readonly FleetMachine[]>> = {
  ftw: [
    { machineNumber: '10001', machineLabel: 'Toyota 8FBE16T · Elektro 1,6 t', dailyRate: 45, weeklyRate: 250, monthlyRate: 850, minimumDays: 1 },
    { machineNumber: '10002', machineLabel: 'Jungheinrich ETV 225 · Schubmast 2,5 t', dailyRate: 58, weeklyRate: 320, monthlyRate: 1100, minimumDays: 3 },
    { machineNumber: '10004', machineLabel: 'Linde H35D · Diesel 3,5 t', dailyRate: 72, weeklyRate: 390, monthlyRate: 1350, minimumDays: 3 },
  ],
  stw: [
    { machineNumber: '20005', machineLabel: 'Kalmar DCE90 · Schwerlast 9 t', dailyRate: 210, weeklyRate: 1150, monthlyRate: 3900, minimumDays: 5 },
  ],
  gsl: [],
};

function fleetItem(fleet: readonly FleetMachine[], machineNumber: string): FleetMachine | undefined {
  return fleet.find((machine) => machine.machineNumber === machineNumber);
}

function buildItem(fleet: readonly FleetMachine[], machineNumber: string, startDate: string, endDate: string, index: number): RentalItem {
  const machine = fleetItem(fleet, machineNumber);
  return {
    id: `item-${machineNumber}-${index}`,
    machineNumber,
    machineLabel: machine?.machineLabel ?? machineNumber,
    dailyRate: machine?.dailyRate ?? 0,
    weeklyRate: machine?.weeklyRate ?? 0,
    monthlyRate: machine?.monthlyRate ?? 0,
    minimumDays: machine?.minimumDays ?? 1,
    startDate,
    endDate,
    actualReturnDate: null,
    meterOut: null,
    meterIn: null,
    damageNote: '',
    damageAmount: 0,
  };
}

/** Baut eine Position aus dem Auftrags-Input: Sätze + Label kommen mit; minimumDays aus der Flotte (Fallback 1). */
function buildItemFromInput(fleet: readonly FleetMachine[], machine: RentalMachineInput, startDate: string, endDate: string, index: number): RentalItem {
  const known = fleetItem(fleet, machine.machineNumber);
  return {
    id: `item-${machine.machineNumber}-${index}`,
    machineNumber: machine.machineNumber,
    machineLabel: machine.machineLabel || known?.machineLabel || machine.machineNumber,
    dailyRate: machine.dailyRate,
    weeklyRate: machine.weeklyRate,
    monthlyRate: machine.monthlyRate,
    minimumDays: known?.minimumDays ?? 1,
    startDate,
    endDate,
    actualReturnDate: null,
    meterOut: null,
    meterIn: null,
    damageNote: '',
    damageAmount: 0,
  };
}

function transport(seq: number, type: RentalTransport['type'], plannedDate: string, status: TransportStatus, driverName = '', completedAt: string | null = null): RentalTransport {
  return { id: `transport-${seq}-${type}`, number: transportNumber(seq), type, plannedDate, driverName, status, completedAt };
}

function seedOrders(tenantId: string): RentalOrder[] {
  if (tenantId === 'ftw') {
    const fleet = fleetByTenant.ftw;
    return [
      {
        id: 'ftw-rental-1', tenantId, number: orderNumber(1), customerNumber: '10001', customerName: 'Werl Industriebedarf GmbH',
        status: 'active', billingStatus: 'open', kind: 'standard', intervalWeekday: null, transportCost: 0, startDate: '2026-07-01', endDate: '2026-09-30',
        siteAddress: 'Industriestraße 2, 59457 Werl', contactName: 'Herr Meyer', contactPhone: '02922 90200',
        deliveryRequired: true, pickupRequired: true, depositAmount: 500, note: 'Lagererweiterung Q3',
        items: [{ ...buildItem(fleet, '10001', '2026-07-01', '2026-09-30', 0), meterOut: 1840 }],
        transports: [transport(1, 'delivery', '2026-07-01', 'done', 'K. Bauer', '2026-07-01T08:30:00.000Z'), transport(2, 'pickup', '2026-09-30', 'open')],
        billedThrough: '2026-07-31', createdAt: '2026-06-25T09:00:00.000Z',
      },
      {
        id: 'ftw-rental-2', tenantId, number: orderNumber(2), customerNumber: '10003', customerName: 'Schulte Baustoffe KG',
        status: 'reserved', billingStatus: 'open', kind: 'standard', intervalWeekday: null, transportCost: 0, startDate: '2026-07-20', endDate: '2026-08-03',
        siteAddress: 'Arnsberger Str. 15, 59759 Arnsberg', contactName: 'Frau Schulte', contactPhone: '02922 90300',
        deliveryRequired: true, pickupRequired: true, depositAmount: 300, note: '',
        items: [buildItem(fleet, '10002', '2026-07-20', '2026-08-03', 0)],
        transports: [transport(3, 'delivery', '2026-07-20', 'scheduled', 'K. Bauer'), transport(4, 'pickup', '2026-08-03', 'open')],
        billedThrough: null, createdAt: '2026-07-08T11:00:00.000Z',
      },
      {
        id: 'ftw-rental-3', tenantId, number: orderNumber(3), customerNumber: '10002', customerName: 'Bäckerei Haverkamp GmbH',
        status: 'request', billingStatus: 'open', kind: 'standard', intervalWeekday: null, transportCost: 0, startDate: '2026-08-01', endDate: '2026-08-15',
        siteAddress: 'Industriestraße 2, 59494 Soest', contactName: 'Frau Kontakt', contactPhone: '02922 80200',
        deliveryRequired: false, pickupRequired: false, depositAmount: 300, note: 'Verfügbarkeit prüfen',
        items: [buildItem(fleet, '10004', '2026-08-01', '2026-08-15', 0)],
        transports: [], billedThrough: null, createdAt: '2026-07-13T14:00:00.000Z',
      },
      {
        id: 'ftw-rental-4', tenantId, number: orderNumber(4), customerNumber: '10002', customerName: 'Bäckerei Haverkamp GmbH',
        status: 'active', billingStatus: 'open', kind: 'interval', intervalWeekday: 5, transportCost: 45,
        startDate: '2026-07-01', endDate: '2026-12-31',
        siteAddress: 'Industriestraße 2, 59494 Soest', contactName: 'Frau Kontakt', contactPhone: '02922 80200',
        deliveryRequired: true, pickupRequired: true, depositAmount: 300, note: 'Jeden Freitag für die Wochenendschicht',
        items: [buildItem(fleet, '10004', '2026-07-01', '2026-12-31', 0)],
        transports: [], billedThrough: null, createdAt: '2026-06-20T09:00:00.000Z',
      },
    ];
  }
  if (tenantId === 'stw') {
    const fleet = fleetByTenant.stw;
    return [{
      id: 'stw-rental-1', tenantId, number: orderNumber(1), customerNumber: '20001', customerName: 'Spedition Nordwest KG',
      status: 'active', billingStatus: 'open', kind: 'standard', intervalWeekday: null, transportCost: 0, startDate: '2026-07-05', endDate: '2026-08-05',
      siteAddress: 'Hafenstraße 8, 59755 Werl', contactName: 'Herr Nord', contactPhone: '02922 55010',
      deliveryRequired: true, pickupRequired: true, depositAmount: 1500, note: '',
      items: [{ ...buildItem(fleet, '20005', '2026-07-05', '2026-08-05', 0), meterOut: 5120 }],
      transports: [transport(1, 'delivery', '2026-07-05', 'done', 'M. Wagner', '2026-07-05T07:00:00.000Z'), transport(2, 'pickup', '2026-08-05', 'open')],
      billedThrough: null, createdAt: '2026-06-30T09:00:00.000Z',
    }];
  }
  return [];
}

export class DemoRentalGateway implements RentalGateway {
  private readonly states = new Map<string, RentalOrder[]>();
  private readonly nextOrder: Record<string, number> = { ftw: 5, stw: 2, gsl: 1 };
  private readonly nextTransport: Record<string, number> = { ftw: 5, stw: 3, gsl: 1 };

  private orders(tenantId: string): RentalOrder[] {
    const existing = this.states.get(tenantId);
    if (existing) return existing;
    const seeded = seedOrders(tenantId);
    this.states.set(tenantId, seeded);
    return seeded;
  }

  async listFleet(tenantId: string): Promise<readonly FleetMachine[]> {
    return fleetByTenant[tenantId] ?? [];
  }

  async listRentalOrders(tenantId: string): Promise<readonly RentalOrder[]> {
    return this.orders(tenantId).slice().sort((left, right) => right.number.localeCompare(left.number));
  }

  async createRentalOrder(input: CreateRentalOrderInput): Promise<RentalOrder> {
    if (input.endDate < input.startDate) throw new Error('Das Mietende darf nicht vor dem Mietbeginn liegen.');
    if (input.machines.length === 0) throw new Error('Bitte mindestens eine Maschine wählen.');
    const orders = this.orders(input.tenantId);
    const anfrage = {
      kind: input.kind ?? 'standard',
      weekday: input.kind === 'interval' ? (input.intervalWeekday ?? 5) : null,
      from: input.startDate, to: input.endDate,
    } as const;
    const doppelt = input.machines.filter((m, i) => input.machines.findIndex((x) => x.machineNumber === m.machineNumber) !== i);
    if (doppelt.length > 0) {
      throw new Error(`Maschine ${doppelt[0].machineNumber} steht zweimal im selben Mietauftrag.`);
    }
    for (const machine of input.machines) {
      const beleger = findBookingClash(orders, machine.machineNumber, anfrage);
      if (beleger) throw new Error(bookingClashMessage(machine.machineNumber, beleger.number));
    }
    const fleet = fleetByTenant[input.tenantId] ?? [];
    const seq = this.nextOrder[input.tenantId] ?? 1;
    this.nextOrder[input.tenantId] = seq + 1;
    const transports: RentalTransport[] = [];
    if (input.deliveryRequired) { const t = this.nextTransport[input.tenantId]++; transports.push(transport(t, 'delivery', input.startDate, 'open')); }
    if (input.pickupRequired) { const t = this.nextTransport[input.tenantId]++; transports.push(transport(t, 'pickup', input.endDate, 'open')); }
    const order: RentalOrder = {
      id: `${input.tenantId}-rental-${seq}-${new Date().toISOString()}`,
      tenantId: input.tenantId, number: orderNumber(seq),
      customerNumber: input.customerNumber, customerName: input.customerName,
      status: input.asOffer ? 'request' : 'reserved', billingStatus: 'open',
      kind: input.kind ?? 'standard',
      intervalWeekday: input.kind === 'interval' ? (input.intervalWeekday ?? 5) : null,
      transportCost: input.transportCost ?? 0,
      startDate: input.startDate, endDate: input.endDate, siteAddress: input.siteAddress,
      contactName: input.contactName, contactPhone: input.contactPhone,
      deliveryRequired: input.deliveryRequired, pickupRequired: input.pickupRequired,
      depositAmount: input.depositAmount, note: input.note,
      items: input.machines.map((machine, index) => buildItemFromInput(fleet, machine, input.startDate, input.endDate, index)),
      transports, billedThrough: null, createdAt: new Date().toISOString(),
    };
    orders.unshift(order);
    return order;
  }

  async updateRentalOrder(input: UpdateRentalOrderInput): Promise<RentalOrder> {
    const { orders, index, order } = this.locate(input.tenantId, input.rentalId);
    if (order.status !== 'request' && order.status !== 'reserved') {
      throw new Error('Nur noch nicht ausgelieferte Mietaufträge (Anfrage/Reserviert) können bearbeitet werden.');
    }
    if (input.endDate < input.startDate) throw new Error('Das Mietende darf nicht vor dem Mietbeginn liegen.');
    if (input.machines.length === 0) throw new Error('Bitte mindestens eine Maschine wählen.');
    const anfrage = {
      kind: input.kind ?? 'standard',
      weekday: (input.kind ?? 'standard') === 'interval' ? (input.intervalWeekday ?? 5) : null,
      from: input.startDate, to: input.endDate,
    } as const;
    const doppelt = input.machines.filter((m, i) => input.machines.findIndex((x) => x.machineNumber === m.machineNumber) !== i);
    if (doppelt.length > 0) {
      throw new Error(`Maschine ${doppelt[0].machineNumber} steht zweimal im selben Mietauftrag.`);
    }
    for (const machine of input.machines) {
      // Verfügbarkeit ohne den Auftrag selbst -- er darf seine eigenen Maschinen behalten.
      const beleger = findBookingClash(orders, machine.machineNumber, anfrage, order.id);
      if (beleger) throw new Error(bookingClashMessage(machine.machineNumber, beleger.number));
    }
    const fleet = fleetByTenant[input.tenantId] ?? [];
    const kind = input.kind ?? 'standard';
    // Transporte neu aufbauen (vor der Auslieferung sind sie noch offen/geplant).
    const transports: RentalTransport[] = [];
    if (input.deliveryRequired) { const t = this.nextTransport[input.tenantId]++; transports.push(transport(t, 'delivery', input.startDate, 'open')); }
    if (input.pickupRequired) { const t = this.nextTransport[input.tenantId]++; transports.push(transport(t, 'pickup', input.endDate, 'open')); }
    const updated: RentalOrder = {
      ...order,
      customerNumber: input.customerNumber, customerName: input.customerName,
      kind, intervalWeekday: kind === 'interval' ? (input.intervalWeekday ?? 5) : null,
      transportCost: input.transportCost ?? 0,
      startDate: input.startDate, endDate: input.endDate, siteAddress: input.siteAddress,
      contactName: input.contactName, contactPhone: input.contactPhone,
      deliveryRequired: input.deliveryRequired, pickupRequired: input.pickupRequired,
      depositAmount: input.depositAmount, note: input.note,
      items: input.machines.map((machine, itemIndex) => buildItemFromInput(fleet, machine, input.startDate, input.endDate, itemIndex)),
      transports,
      // number, status, billingStatus, billedThrough, createdAt bleiben erhalten.
    };
    orders[index] = updated;
    return updated;
  }

  async transitionRental({ tenantId, rentalId, toStatus }: { tenantId: string; rentalId: string; toStatus: RentalStatus }): Promise<RentalOrder> {
    const { orders, index, order } = this.locate(tenantId, rentalId);
    if (!allowedRentalTransitionsFor(order.status).includes(toStatus)) {
      throw new Error(`Der Statuswechsel von ${order.status} zu ${toStatus} ist nicht zulässig.`);
    }
    // Mietangebot annehmen (Anfrage -> Reserviert): ein Angebot reserviert nichts, also erst hier prüfen,
    // ob die Maschinen im Zeitraum noch frei sind (der eigene Auftrag zählt nicht als Beleger).
    if (order.status === 'request' && toStatus === 'reserved') {
      for (const item of order.items) {
        const beleger = findBookingClash(
          orders, item.machineNumber,
          { kind: order.kind, weekday: order.intervalWeekday, from: item.startDate, to: item.endDate },
          order.id,
        );
        if (beleger) {
          throw new Error(`Maschine ${item.machineNumber} ist im Mietzeitraum inzwischen belegt (${beleger.number}) und kann nicht bestätigt werden.`);
        }
      }
    }
    let transports = order.transports;
    if (toStatus === 'active') transports = markTransport(order.transports, 'delivery');
    const updated: RentalOrder = {
      ...order,
      status: toStatus,
      transports,
      billingStatus: toStatus === 'completed' ? 'ready' : order.billingStatus,
    };
    orders[index] = updated;
    return updated;
  }

  async updateTransport({ tenantId, rentalId, transportId, plannedDate, driverName, status }: { tenantId: string; rentalId: string; transportId: string; plannedDate?: string; driverName?: string; status?: TransportStatus }): Promise<RentalOrder> {
    const { orders, index, order } = this.locate(tenantId, rentalId);
    const updated: RentalOrder = {
      ...order,
      transports: order.transports.map((item) => item.id === transportId ? {
        ...item,
        plannedDate: plannedDate ?? item.plannedDate,
        driverName: driverName ?? item.driverName,
        status: status ?? item.status,
        completedAt: status === 'done' ? new Date().toISOString() : item.completedAt,
      } : item),
    };
    orders[index] = updated;
    return updated;
  }

  async captureReturn({ tenantId, rentalId, actualReturnDate, items }: { tenantId: string; rentalId: string; actualReturnDate: string; items: readonly ReturnItemInput[] }): Promise<RentalOrder> {
    const { orders, index, order } = this.locate(tenantId, rentalId);
    const byId = new Map(items.map((entry) => [entry.itemId, entry]));
    const updated: RentalOrder = {
      ...order,
      status: 'completed',
      billingStatus: 'ready',
      transports: markTransport(order.transports, 'pickup'),
      items: order.items.map((item) => {
        const capture = byId.get(item.id);
        return capture ? { ...item, actualReturnDate, meterIn: capture.meterIn, damageNote: capture.damageNote.trim(), damageAmount: round2(Math.max(0, capture.damageAmount)) } : item;
      }),
    };
    orders[index] = updated;
    return updated;
  }

  async terminateEarly({ tenantId, rentalId, terminationDate }: { tenantId: string; rentalId: string; terminationDate: string }): Promise<RentalOrder> {
    const { orders, index, order } = this.locate(tenantId, rentalId);
    if (order.status !== 'active' && order.status !== 'reserved') {
      throw new Error('Nur laufende oder reservierte Mieten können vorzeitig beendet werden.');
    }
    if (terminationDate < order.startDate) throw new Error('Das Beendigungsdatum darf nicht vor dem Mietbeginn liegen.');
    const updated: RentalOrder = {
      ...order,
      status: 'return_scheduled',
      endDate: terminationDate,
      items: order.items.map((item) => ({ ...item, endDate: minDate(item.endDate, terminationDate) })),
      transports: order.transports.map((t) => t.type === 'pickup' && t.status !== 'done' ? { ...t, plannedDate: terminationDate, status: 'scheduled' } : t),
    };
    orders[index] = updated;
    return updated;
  }

  async billMonth({ tenantId, rentalId }: { tenantId: string; rentalId: string }): Promise<{ order: RentalOrder; billing: RentalBilling }> {
    const { orders, index, order } = this.locate(tenantId, rentalId);
    const { to, billing } = computeMonthlyBilling(order);
    orders[index] = { ...order, billedThrough: to };
    return { order: orders[index], billing };
  }

  async billFinal({ tenantId, rentalId }: { tenantId: string; rentalId: string }): Promise<{ order: RentalOrder; billing: RentalBilling }> {
    const { orders, index, order } = this.locate(tenantId, rentalId);
    const billing = computeFinalBilling(order);
    orders[index] = { ...order, billingStatus: 'billed' };
    return { order: orders[index], billing };
  }

  private locate(tenantId: string, rentalId: string): { orders: RentalOrder[]; index: number; order: RentalOrder } {
    const orders = this.orders(tenantId);
    const index = orders.findIndex((order) => order.id === rentalId);
    if (index < 0) throw new Error('Der Mietauftrag wurde in diesem Mandanten nicht gefunden.');
    return { orders, index, order: orders[index] };
  }
}

function markTransport(transports: readonly RentalTransport[], type: RentalTransport['type']): readonly RentalTransport[] {
  return transports.map((item) => item.type === type && item.status !== 'done' ? { ...item, status: 'done', completedAt: new Date().toISOString() } : item);
}

function minDate(a: string, b: string): string {
  return a < b ? a : b;
}


export function createDemoRentalGateway(): RentalGateway {
  return new DemoRentalGateway();
}
